// drawer/components/ThreatIntelDrawerSkeleton.tsx
import { Container } from '@/components/legacy-ui';
import { cn } from '@/utils/classname';

/**
 * Skeleton line for text/shape placeholders.
 */
function SkeletonLine({ className }: { className?: string }) {
  return (
    <div
      aria-hidden="true"
      className={cn('bg-chariot-layer2 animate-pulse rounded', className)}
    />
  );
}

/**
 * Loading skeleton that mirrors the ThreatIntelDrawer's Overview layout so the
 * loading footprint matches the real content and the view does not jump/resize
 * when data arrives.
 *
 * Rendered while the vulnerability record is still fetching so users never see
 * the empty-state placeholders ("No information available at this time", blank
 * description) flash before real data arrives. The drawer's data fetch is a
 * plain TanStack query (not a Suspense query), so the per-tab `Suspense`
 * fallbacks never cover it — this skeleton is the actual data-loading gate.
 *
 * Structure intentionally tracks the loaded layout 1:1: title, CVE tag pill,
 * Send Notification action, then the bordered content `Container` holding the
 * tabs row, description, and the tall EPSS section (legend + percentile card +
 * 300px chart). It reuses the real `Container` so border/padding stay in sync
 * with the loaded state.
 */
export function ThreatIntelDrawerSkeleton() {
  return (
    <div
      data-testid="threat-intel-drawer-skeleton"
      role="status"
      aria-busy="true"
      className="flex flex-col gap-6"
    >
      <span className="sr-only">Loading threat intelligence…</span>

      {/* Title */}
      <SkeletonLine className="h-7 w-2/3" />

      {/* CVE tag pill (CVE-ID | Published | Modified) */}
      <SkeletonLine className="h-7 w-80 max-w-full rounded-full" />

      {/* Send Notification action */}
      <SkeletonLine className="h-8 w-40 rounded-md" />

      {/* Content container mirrors the loaded <Container> (rounded-md border p-6) */}
      <Container className="flex flex-col gap-6">
        {/* Tabs row + underline divider */}
        <div className="border-chariot-stroke flex gap-6 border-b pb-3">
          <SkeletonLine className="h-4 w-20" />
          <SkeletonLine className="h-4 w-36" />
          <SkeletonLine className="h-4 w-16" />
        </div>

        {/* Description (h3 text-sm, ~2 lines) */}
        <div className="flex flex-col gap-2">
          <SkeletonLine className="h-4 w-full" />
          <SkeletonLine className="h-4 w-5/6" />
        </div>

        {/* EPSS section: heading + card mirroring EPSSComponent */}
        <div className="flex flex-col gap-2">
          {/* "EPSS" h4 text-lg font-bold */}
          <SkeletonLine className="h-6 w-20" />
          <Container className="p-5">
            {/* Legend (left) + Percentile card (right) */}
            <div className="mb-8 flex flex-wrap items-start justify-between gap-4">
              <SkeletonLine className="h-24 w-full max-w-[calc(100%-200px)] rounded-lg" />
              <SkeletonLine className="h-[150px] w-[180px] shrink-0 rounded-xl" />
            </div>
            {/* Chart area (EpssChart renders at height={300}) */}
            <SkeletonLine className="h-[300px] w-full rounded-lg" />
          </Container>
        </div>
      </Container>
    </div>
  );
}
